import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import { BrandLogo, LogoImage } from "../components/BrandLogo";
import { ModalShell } from "../components/ModalShell";
import { ReactivateAccountPanel } from "../components/ReactivateAccountPanel";
import { LEGAL_CONFIG } from "../config/legalConfig";
import { BrandButton } from "../components/brand";
import { C, font, radii, shadows, space, type } from "../styles/tokens";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [resumeNeeded, setResumeNeeded] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [legalModal, setLegalModal] = useState(null);
  // Set when the account was deleted by its owner: { token, email, deletedAt }.
  const [reactivation, setReactivation] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout, adoptSession } = useAuth();

  // Auto-dismiss error toast after 4 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const searchParams = new URLSearchParams(location.search);
  const nextFromQuery = searchParams.get("next");
  const redirectTo =
    nextFromQuery || location.state?.from?.pathname || "/profile";

  // Where a signed-in person lands: the page they were sent from, or their
  // role's home. Used after signing in and after reactivating an account.
  function goToLanding(me) {
    // Step 3: Clear any stale registration draft keys since user profile exists
    try {
      localStorage.removeItem("tickandpass:officer-draft:INSTITUTION_OFFICER");
      localStorage.removeItem("tickandpass:officer-draft:ORGANISATION_OFFICER");
    } catch {
      /* non-fatal */
    }

    // Step 4: navigate to the right landing for this user's role.
    let target = redirectTo;
    if (target === "/profile") {
      if (me.type === "PLATFORM_ADMIN") {
        target = "/admin";
      } else if (me.type === "INSTITUTION_OFFICER") {
        if (me.institutionOfficer) {
          const instStatus = me.institutionOfficer.institution?.status;
          target =
            instStatus === "APPROVED" ? "/dashboard" : "/awaiting-approval";
        } else {
          target = "/register/institution/details";
        }
      } else if (me.type === "ORGANISATION_OFFICER") {
        if (me.organisationOfficer) {
          const orgStatus = me.organisationOfficer.organisation?.status;
          target =
            orgStatus === "APPROVED" ? "/dashboard" : "/awaiting-approval";
        } else {
          target = "/register/organisation/details";
        }
      }
    }
    navigate(target, { replace: true });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const me = await login(email, password);

      // Step 2b: Deferred-officer flow resume check
      if (!me) {
        const hasInstDraft = !!localStorage.getItem(
          "tickandpass:officer-draft:INSTITUTION_OFFICER",
        );
        const hasOrgDraft = !!localStorage.getItem(
          "tickandpass:officer-draft:ORGANISATION_OFFICER",
        );

        if (hasInstDraft || hasOrgDraft) {
          if (hasInstDraft && !hasOrgDraft) {
            navigate("/register/institution/details", { replace: true });
            return;
          }
          if (hasOrgDraft && !hasInstDraft) {
            navigate("/register/organisation/details", { replace: true });
            return;
          }
          setResumeNeeded(true);
          setSubmitting(false);
          return;
        }

        try {
          await logout();
        } catch {
          /* non-fatal */
        }
        setError(
          "We signed you in but couldn't load your account. Please try again. If this keeps happening, contact support.",
        );
        setSubmitting(false);
        return;
      }

      goToLanding(me);
    } catch (err) {
      if (err?.code === "ACCOUNT_DELETED") {
        // The right password on an account its owner deleted.
        setReactivation({
          token: err.details?.reactivationToken,
          email: err.details?.email || email,
          deletedAt: err.details?.deletedAt,
        });
        return;
      }
      setError(
        err?.message ||
          err?.details ||
          "Sign in failed. Please check your credentials.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: C.background,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        fontFamily: font,
        color: C.textStrong,
        boxSizing: "border-box",
      }}
    >
      <main style={{ width: "100%", maxWidth: 960, margin: "0 auto" }}>
        {/* Centered Brand Lockup per Section 7 */}
        <div style={{ textAlign: "center", marginBottom: space.xl }}>
          <Link to="/" style={{ display: "inline-block" }}>
            <img
              src="/brand-lockup.png"
              alt="TickAndPass"
              style={{ width: 200, height: "auto", objectFit: "contain" }}
            />
          </Link>
        </div>

        <div
          style={{
            backgroundColor: C.surface,
            borderRadius: radii.card,
            boxShadow: shadows.card,
            overflow: "hidden",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          }}
        >
          {/* Left Decorative/Branding Panel */}
          <section
            style={{
              padding: space.xxxl,
              backgroundColor: C.surfaceSunken,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              borderRight: `1px solid ${C.divider}`,
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-block",
                  backgroundColor: C.primaryTint,
                  color: C.primary,
                  fontSize: type.overline.fontSize,
                  fontWeight: type.overline.fontWeight,
                  letterSpacing: type.overline.letterSpacing,
                  textTransform: type.overline.textTransform,
                  padding: "4px 10px",
                  borderRadius: radii.control,
                  marginBottom: space.md,
                }}
              >
                Verification-first platform
              </div>
              <h1
                style={{
                  fontSize: type.display.fontSize,
                  lineHeight: type.display.lineHeight,
                  fontWeight: type.display.fontWeight,
                  color: C.navy,
                  margin: `0 0 ${space.md}px`,
                  letterSpacing: type.display.letterSpacing,
                }}
              >
                Verified once, at source.
                <span style={{ color: C.primary, display: "block" }}>
                  Trusted everywhere.
                </span>
              </h1>
              <p
                style={{
                  fontSize: type.body.fontSize,
                  lineHeight: type.body.lineHeight,
                  color: C.textMuted,
                  margin: 0,
                  maxWidth: 360,
                }}
              >
                Sign in securely to continue to your professional or officer account.
              </p>
            </div>
          </section>

          {/* Right Authentication Panel */}
          <section
            style={{
              padding: space.xxxl,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div style={{ width: "100%", maxWidth: 380 }}>
              {reactivation ? (
                <ReactivateAccountPanel
                  token={reactivation.token}
                  email={reactivation.email}
                  deletedAt={reactivation.deletedAt}
                  onReactivated={async (session) =>
                    goToLanding(await adoptSession(session))
                  }
                  onCancel={() => {
                    setReactivation(null);
                    setPassword("");
                  }}
                />
              ) : (
                <>
                  <div style={{ marginBottom: space.xl }}>
                    <h2
                      style={{
                        fontSize: type.title.fontSize,
                        lineHeight: type.title.lineHeight,
                        fontWeight: type.title.fontWeight,
                        color: C.navy,
                        margin: `0 0 ${space.xs}px`,
                      }}
                    >
                      Sign in
                    </h2>
                    <p
                      style={{
                        fontSize: type.body.fontSize,
                        lineHeight: type.body.lineHeight,
                        color: C.textMuted,
                        margin: 0,
                      }}
                    >
                      Enter your details to access your account
                    </p>
                  </div>

                  <form onSubmit={handleSubmit}>
                    {/* Email Field */}
                    <div style={{ marginBottom: space.md }}>
                      <label
                        htmlFor="email"
                        style={{
                          display: "block",
                          fontSize: type.label.fontSize,
                          lineHeight: type.label.lineHeight,
                          fontWeight: type.label.fontWeight,
                          color: C.textMuted,
                          marginBottom: space.sm,
                        }}
                      >
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (resumeNeeded) setResumeNeeded(false);
                        }}
                        required
                        style={{
                          width: "100%",
                          height: 48,
                          boxSizing: "border-box",
                          padding: "0 16px",
                          fontSize: type.body.fontSize,
                          fontFamily: font,
                          color: C.textStrong,
                          backgroundColor: C.surface,
                          border: `1.5px solid ${C.border}`,
                          borderRadius: radii.control,
                          outline: "none",
                          transition: "border-color 0.15s ease",
                        }}
                      />
                    </div>

                    {/* Password Field */}
                    <div style={{ marginBottom: space.lg }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: space.sm,
                        }}
                      >
                        <label
                          htmlFor="password"
                          style={{
                            fontSize: type.label.fontSize,
                            lineHeight: type.label.lineHeight,
                            fontWeight: type.label.fontWeight,
                            color: C.textMuted,
                          }}
                        >
                          Password
                        </label>
                        <Link
                          to="/forgot-password"
                          style={{
                            fontSize: type.caption.fontSize,
                            color: C.primary,
                            textDecoration: "none",
                            fontWeight: 600,
                          }}
                        >
                          Forgot password?
                        </Link>
                      </div>
                      <div style={{ position: "relative" }}>
                        <input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="current-password"
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (resumeNeeded) setResumeNeeded(false);
                          }}
                          required
                          style={{
                            width: "100%",
                            height: 48,
                            boxSizing: "border-box",
                            padding: "0 44px 0 16px",
                            fontSize: type.body.fontSize,
                            fontFamily: font,
                            color: C.textStrong,
                            backgroundColor: C.surface,
                            border: `1.5px solid ${C.border}`,
                            borderRadius: radii.control,
                            outline: "none",
                            transition: "border-color 0.15s ease",
                          }}
                        />
                        <button
                          type="button"
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: 44,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "transparent",
                            border: "none",
                            color: C.textSubtle,
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          {showPassword ? (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="m3 3 18 18" />
                              <path d="M10.6 5.2A10.7 10.7 0 0 1 12 5c6 0 9.5 7 9.5 7a17.5 17.5 0 0 1-3.1 3.9" />
                              <path d="M6.2 6.3C3.9 8 2.5 12 2.5 12s3.5 7 9.5 7c1.5 0 2.8-.4 4-1" />
                              <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
                            </svg>
                          ) : (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6Z" />
                              <circle cx="12" cy="12" r="2.5" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Resume Notice if present */}
                    {resumeNeeded && (
                      <div
                        style={{
                          backgroundColor: C.warningTint,
                          color: C.warning,
                          padding: space.md,
                          borderRadius: radii.control,
                          marginBottom: space.md,
                          fontSize: type.caption.fontSize,
                          lineHeight: type.caption.lineHeight,
                        }}
                      >
                        <p style={{ fontWeight: 600, margin: "0 0 4px" }}>
                          One last step to finish your sign-up
                        </p>
                        <p style={{ margin: "0 0 12px" }}>
                          We've verified your email and phone. Click below to complete your onboarding details.
                        </p>
                        <div style={{ display: "flex", gap: space.sm }}>
                          <BrandButton
                            size="small"
                            variant="primary"
                            onClick={() =>
                              navigate("/register/institution/details", {
                                replace: true,
                              })
                            }
                          >
                            Finish institution
                          </BrandButton>
                          <BrandButton
                            size="small"
                            variant="primary"
                            onClick={() =>
                              navigate("/register/organisation/details", {
                                replace: true,
                              })
                            }
                          >
                            Finish organisation
                          </BrandButton>
                        </div>
                      </div>
                    )}

                    {error && (
                      <div
                        style={{
                          backgroundColor: C.dangerTint,
                          color: C.danger,
                          padding: `${space.sm}px ${space.md}px`,
                          borderRadius: radii.control,
                          marginBottom: space.md,
                          fontSize: type.caption.fontSize,
                          lineHeight: type.caption.lineHeight,
                        }}
                      >
                        {error}
                      </div>
                    )}

                    <BrandButton
                      type="submit"
                      variant="primary"
                      fullWidth
                      loading={submitting}
                      disabled={resumeNeeded}
                    >
                      Sign in
                    </BrandButton>
                  </form>

                  <p
                    style={{
                      marginTop: space.lg,
                      textAlign: "center",
                      fontSize: type.body.fontSize,
                      color: C.textMuted,
                    }}
                  >
                    Need an account?{" "}
                    <Link
                      to={
                        nextFromQuery
                          ? `/register?next=${encodeURIComponent(nextFromQuery)}`
                          : "/register"
                      }
                      style={{
                        color: C.primary,
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                    >
                      Create an account
                    </Link>
                  </p>

                  <p
                    style={{
                      marginTop: space.xl,
                      textAlign: "center",
                      fontSize: type.caption.fontSize,
                      color: C.textSubtle,
                      lineHeight: type.caption.lineHeight,
                    }}
                  >
                    By continuing, you agree to the{" "}
                    <button
                      type="button"
                      onClick={() => setLegalModal("terms")}
                      style={{
                        background: "none",
                        border: "none",
                        color: C.textMuted,
                        cursor: "pointer",
                        textDecoration: "underline",
                        padding: 0,
                        font: "inherit",
                      }}
                    >
                      Terms
                    </button>{" "}
                    and{" "}
                    <button
                      type="button"
                      onClick={() => setLegalModal("privacy")}
                      style={{
                        background: "none",
                        border: "none",
                        color: C.textMuted,
                        cursor: "pointer",
                        textDecoration: "underline",
                        padding: 0,
                        font: "inherit",
                      }}
                    >
                      Privacy Policy
                    </button>
                  </p>
                </>
              )}
            </div>
          </section>
        </div>
      </main>

      <footer className="hidden mx-auto flex w-full max-w-[1240px] flex-col gap-4 px-4 pb-4 text-[10px] text-[var(--color-muted)] sm:flex-row sm:items-end sm:justify-between sm:px-6 sm:pb-6 sm:text-[11px] lg:px-10">
        {/* Bottom Corner Left: Logo, Company Name, Address */}
        <div className="flex flex-col gap-1 max-w-[420px]">
          <div className="mb-0.5">
            <LogoImage variant="lockup" height={22} />
          </div>
          <div className="font-bold text-[var(--color-text)]">
            Tick and Pass Services Private Limited
          </div>
          <div className="mt-0.5">
            24/SF1 &amp; 63/SF1, Indira Gandhi Street, Kaveri Rangan Nagar,
            Saligramam, Chennai - 600093, Tamil Nadu
          </div>
        </div>

        {/* Middle: Copyright */}
        <div className="text-center sm:pb-0.5">
          &copy; {new Date().getFullYear()} TickAndPass
        </div>

        {/* Bottom Right: Support */}
        <div className="flex flex-col sm:items-end sm:pb-0.5">
          <a
            href="mailto:support@tickandpass.com"
            className="hover:underline text-[var(--color-text)] font-semibold"
          >
            support@tickandpass.com
          </a>
          <span className="hidden sm:block text-[10px]">
            Secure access for every step ahead.
          </span>
        </div>
      </footer>

      {/* Floating Toast Notification (Toaster) */}
      {error && (
        <>
          <style>{`
            @keyframes toastSlideIn {
              from { transform: translateY(-20px) scale(0.95); opacity: 0; }
              to { transform: translateY(0) scale(1); opacity: 1; }
            }
            .animate-toast {
              animation: toastSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            }
          `}</style>
          <div className="fixed top-6 right-6 z-[9999] flex w-[calc(100%-48px)] max-w-[360px] animate-toast items-start gap-3 rounded-xl border border-red-200 bg-white p-4 shadow-[0_16px_48px_rgba(220,38,38,0.12)] sm:right-8 sm:top-8">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-red-800">Error</p>
              <p className="mt-0.5 text-[11px] font-medium leading-relaxed text-red-700">
                {error}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </>
      )}
      {/* Legal Content Modal (Terms & Conditions / Privacy Policy) */}
      {legalModal && (
        <ModalShell
          title={
            legalModal === "terms" ? "Terms & Conditions" : "Privacy Policy"
          }
          subtitle="Tick and Pass Services Private Limited"
          onClose={() => setLegalModal(null)}
          maxWidth="max-w-3xl"
        >
          {/* Modal Tabs */}
          <div className="flex border-b border-slate-200 bg-slate-50 px-6 pt-2 shrink-0">
            <button
              type="button"
              onClick={() => setLegalModal("terms")}
              className={`pb-3 pt-2 text-xs sm:text-sm font-semibold border-b-2 mr-6 transition-colors cursor-pointer ${
                legalModal === "terms"
                  ? "border-[#2563eb] text-[#2563eb]"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              Terms &amp; Conditions
            </button>
            <button
              type="button"
              onClick={() => setLegalModal("privacy")}
              className={`pb-3 pt-2 text-xs sm:text-sm font-semibold border-b-2 transition-colors cursor-pointer ${
                legalModal === "privacy"
                  ? "border-[#2563eb] text-[#2563eb]"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              Privacy Policy
            </button>
          </div>

          {/* Scrollable Document Content */}
          <div className="max-h-[60vh] overflow-y-auto px-6 py-5 text-xs sm:text-sm leading-relaxed text-slate-700 space-y-4">
            {legalModal === "terms" ? (
              <>
                <section>
                  <h3 className="text-sm sm:text-base font-bold text-[#16294C] mb-1">
                    1. Introduction &amp; Acceptance
                  </h3>
                  <p>
                    Welcome to TickAndPass, operated by Tick and Pass Services
                    Private Limited. By accessing, registering for, or using our
                    Platform, you agree to be bound by these Terms &amp;
                    Conditions and our Privacy Policy.
                  </p>
                </section>

                <section>
                  <h3 className="text-sm sm:text-base font-bold text-[#16294C] mb-1">
                    2. User Profiles &amp; Authentication
                  </h3>
                  <p>
                    Users may register as Professionals, Academic Institution
                    Officers, or Organisation Officers. You represent that all
                    identity documents (including Aadhaar and GST verification
                    data) provided during registration are accurate, current,
                    and verifiable.
                  </p>
                </section>

                <section>
                  <h3 className="text-sm sm:text-base font-bold text-[#16294C] mb-1">
                    3. Credential Verification Services
                  </h3>
                  <p>
                    TickAndPass facilitates source-level credential verification
                    between professionals, issuing institutions, and employers.
                    Submission of fraudulent records or forged documents will
                    result in permanent account termination.
                  </p>
                </section>

                <section>
                  <h3 className="text-sm sm:text-base font-bold text-[#16294C] mb-1">
                    4. Fees, Billing &amp; Invoices
                  </h3>
                  <p>
                    Verification requests and memberships are subject to stated
                    pricing in INR. All transactions are processed securely via
                    authorised payment gateways. GST-compliant tax invoices are
                    issued automatically upon confirmation.
                  </p>
                </section>

                <section>
                  <h3 className="text-sm sm:text-base font-bold text-[#16294C] mb-1">
                    5. Governing Law &amp; Jurisdiction
                  </h3>
                  <p>
                    These Terms are governed by the laws of India. Any disputes
                    arising out of or relating to these Terms shall be subject
                    to the exclusive jurisdiction of the courts in Chennai,
                    Tamil Nadu.
                  </p>
                </section>
              </>
            ) : (
              <>
                <section>
                  <h3 className="text-sm sm:text-base font-bold text-[#16294C] mb-1">
                    1. Information We Collect
                  </h3>
                  <p>
                    We collect identification details (name, email, verified
                    mobile number), identity verification tokens (Aadhaar /
                    DigiLocker / GSTIN records), and professional credentials
                    (education degrees, experience records, certificates).
                  </p>
                </section>

                <section>
                  <h3 className="text-sm sm:text-base font-bold text-[#16294C] mb-1">
                    2. How We Use Your Data
                  </h3>
                  <p>
                    Your data is strictly used for source-level verification
                    with issuing authorities, fraud prevention, profile sharing
                    that you explicitly approve, and generating legally valid
                    audit receipts.
                  </p>
                </section>

                <section>
                  <h3 className="text-sm sm:text-base font-bold text-[#16294C] mb-1">
                    3. Data Protection &amp; Sharing
                  </h3>
                  <p>
                    All sensitive uploaded documents are encrypted with
                    tokenized HMAC signed URLs. We do not sell or monetize
                    personal user information with third-party advertisers.
                  </p>
                </section>

                <section>
                  <h3 className="text-sm sm:text-base font-bold text-[#16294C] mb-1">
                    4. Grievance &amp; Contact
                  </h3>
                  <p>
                    For privacy inquiries or deletion requests, contact our
                    Grievance Officer at{" "}
                    <a
                      href={`mailto:${LEGAL_CONFIG.privacyEmail}`}
                      className="text-[#2563eb] font-semibold underline"
                    >
                      {LEGAL_CONFIG.privacyEmail}
                    </a>
                    , Saligramam, Chennai - 600093, Tamil Nadu.
                  </p>
                </section>
              </>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
            <span className="text-[11px] text-slate-500">
              Version 1.0 - Tick &amp; Pass Services Pvt. Ltd.
            </span>
            <button
              type="button"
              onClick={() => setLegalModal(null)}
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-[#2563eb] text-white hover:bg-blue-700 transition-colors shadow-sm cursor-pointer"
            >
              Close
            </button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
